import React, { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Lightweight cookie/preferences consent banner.
 *
 * Stores user's choice in localStorage under `taylordarms:cookie-consent`:
 *  - 'accepted' → analytics + preference cookies allowed (e.g. remember admin tab,
 *    remember dismissed banners on public site, retain gallery filters)
 *  - 'declined' → only strictly-necessary state is kept (auth token + cart-like state)
 *
 * Other components can call hasCookieConsent() before persisting non-essential prefs.
 */

const KEY = 'taylordarms:cookie-consent';

export const hasCookieConsent = () => {
  try { return localStorage.getItem(KEY) === 'accepted'; } catch { return false; }
};

const CookieConsent = () => {
  const [decision, setDecision] = useState(() => {
    try { return localStorage.getItem(KEY); } catch { return null; }
  });

  // Don't render until next tick so SSR/static export doesn't flash the banner
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const remember = (choice) => {
    // Safari private-mode / disabled storage throws on setItem — non-fatal: state still updates.
    try { localStorage.setItem(KEY, choice); } catch { /* storage disabled */ }
    setDecision(choice);
  };

  if (!mounted || decision) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[60] animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <div className="rounded-xl border border-amber-500/40 bg-slate-900/95 backdrop-blur shadow-2xl shadow-black/50 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
            <Cookie className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-white mb-1">We use cookies to keep things smooth</h3>
            <p className="text-xs text-gray-300 leading-relaxed">
              We use cookies to remember your preferences (like the gallery view) and to keep you
              signed in to the admin panel. No tracking, no third-party ads. Choose what works for you.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                data-testid="cookie-consent-accept"
                size="sm"
                onClick={() => remember('accepted')}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                Accept all
              </Button>
              <Button
                data-testid="cookie-consent-decline"
                size="sm"
                variant="outline"
                onClick={() => remember('declined')}
                className="border-slate-700 text-gray-200 hover:bg-slate-800"
              >
                Essential only
              </Button>
            </div>
          </div>
          <button
            data-testid="cookie-consent-dismiss"
            type="button"
            onClick={() => remember('declined')}
            aria-label="Dismiss"
            className="flex-shrink-0 text-gray-500 hover:text-white transition-colors -mt-1 -mr-1 p-1"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
